import { Button } from 'components/common/button/Button';
import { reaction, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import moment from 'moment';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { DATE_FORMAT_SHORTER } from '../../../config';
import { InfoFile } from '../../../server-api/model';
import { filesContext } from '../../../state/filesState';
import { GenericSorter } from '../../../state/genericSorter';
import { modalContext } from '../../../state/modalState';
import { toastSubject } from '../../../state/rxjs';
import { DashedBox, IconRotate, IconRotateFlipped } from '../../common/icons';
import { Spinner } from '../../common/spinner/Spinner';
import { SortHeaderCell } from '../../common/table/SortHeaderCell';
import { useOnKeySelect } from 'utils/hooks';

export const Files = observer(() => {
  const filesState = useContext(filesContext);
  const dragboxRef = useRef(null);
  const bigImgRef = useRef<HTMLImageElement>(null);
  const smallImgRef = useRef<HTMLDivElement>(null);
  const [rotation, setRotation] = useState(0);
  const [sorter, updateSorter] = useState(
    new GenericSorter(filesState.files, 'fileDate', false)
  );
  const [shownFiles, setShownCases] = useState<InfoFile[]>(
    sorter.sortedEntries
  );
  const [imgExpanded, setImgExpanded] = useState(false);
  const modalState = useContext(modalContext);
  const [tableFocused, setTableFocused] = useState(false);
  const tableRef = useRef<HTMLTableElement>(null);
  const rowRefs = useRef<HTMLTableRowElement[]>([]);

  useOnKeySelect(tableFocused, tableRef, 'tr.file-row');

  useEffect(() => {
    const disposer = reaction(
      () => {
        return [sorter.sortBy, sorter.sortAscending];
      },
      (data, reaction) => {
        setShownCases(sorter.sortedEntries);
      }
    );
    return disposer;
  }, [sorter]);

  useEffect(() => {
    const newSorter = new GenericSorter(filesState.files, 'fileDate', false);
    updateSorter(newSorter);
    setShownCases(newSorter.sortedEntries);
  }, [filesState.files]);

  const handleBlur = (e: React.FocusEvent) => {
    if (!rowRefs.current?.includes(e.relatedTarget as any)) {
      setTableFocused(false);
    }
  }

  const handleFocus = (e: React.FocusEvent) => {
    if (rowRefs.current?.includes(e.target as any)) {
      setTableFocused(true);
    }
  }

  let dragCounter = 0;

  const dragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.classList.add('dragover');
    dragCounter++;
  };

  const dragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    dragCounter--;
    if (dragCounter === 0) {
      e.currentTarget.classList.remove('dragover');
    }
  };

  const dragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const dropHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const files = e.dataTransfer.files;
    runInAction(() => {
      filesState.droppedFile = files[0];
    });
    dragCounter = 0;
    e.currentTarget.classList.remove('dragover');
    modalState.toggleModal('filesUploadFile', true);
  };

  const rotateRight = (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setRotation(rotation + 90);
  };

  const rotateLeft = (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setRotation(rotation - 90);
  };

  const openFile = (file: InfoFile) => {
    filesState.downloadFile(file);
    /*  const link = document.createElement("a");
    link.href = addGetParams(API_URL + "/api/download", {
      f: file.fileDescriptor.id,
      s: window.localStorage.getItem("sessionId")
    });
    link.target = "_blank";
    link.click(); */
  };

  const openUploadModal = () => {
    if (filesState.selectedCases.size == 1) {
      modalState.toggleModal('filesUploadFile', true);
    } else {
      toastSubject.next('Please select a single case for loading the file against');
    }
  };

  const toggleExpandImg = () => {
    setImgExpanded(!imgExpanded);
  };

  const getSmallScale = () => {
    if ((rotation / 90) % 2) {
      if (!smallImgRef.current) {
        return 1;
      }
      const width = smallImgRef.current.clientWidth;
      const height = smallImgRef.current.clientHeight;

      return width < height ? width / height : height / width;
    }
    return 1;
  };

  const getBigScale = () => {
    if ((rotation / 90) % 2) {
      if (!bigImgRef.current) {
        return 1;
      }
      const width = bigImgRef.current.clientWidth;
      const height = bigImgRef.current.clientHeight;
      return width < height ? width / height : height / width;
    }
    return 1;
  };

  return (
    <div className='files'>
      <h2 id='uploadedFilesLabel'>Uploaded files</h2>
      <div className='flex-group2'>
        <div className='files-table-wrapper'>
          <table
            aria-labelledby='uploadedFilesLabel'
            className='table files-table'
            ref={tableRef}
          >
            <thead>
              <tr>
                <SortHeaderCell
                  sorter={sorter}
                  type='fileDate'
                  text='Date'
                  className='file-cell-date'
                  mode='td'
                />
                <SortHeaderCell
                  sorter={sorter}
                  type='fileType'
                  text='Type'
                  className='file-cell-type'
                  mode='td'
                />
                <SortHeaderCell
                  sorter={sorter}
                  type='fileFilename'
                  text='Filename'
                  className='file-cell-name'
                  mode='td'
                />
              </tr>
            </thead>

            <tbody>
              {shownFiles.map((file, index) => {
                return (
                  <tr
                    className={
                      'file-row ' +
                      (file === filesState.selectedFile ? 'selected' : '')
                    }
                    key={'file' + index}
                    onClick={() => filesState.selectFile(file)}
                    aria-selected={file === filesState.selectedFile}
                    tabIndex={(index == 0 && filesState.selectedFile == null) || (file === filesState.selectedFile) ? 0 : -1}
                    ref={(el) => (rowRefs.current[index] = el as HTMLTableRowElement)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  >
                    <td className='file-cell-date'>
                      {moment(file.uploadDate).format(DATE_FORMAT_SHORTER)}
                    </td>
                    <td className='file-cell-type'>
                      {file.debtorFileType}
                    </td>
                    <td className='file-cell-name'>
                      <button className='btn-linkified' onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        openFile(file);
                      }}
                      >
                        {file.fileDescriptor.name}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filesState.loadingFiles ? <Spinner blocking={true} /> : null}
        </div>
        <div className='file-preview'>
          <div
            ref={smallImgRef}
            className={'preview-window'}
            style={{
              transform:
                'rotate(' + rotation + 'deg) scale(' + getSmallScale() + ')',
            }}
          >
            {filesState.fileToShowURL ? (
              <img
                onClick={toggleExpandImg}
                alt='preview'
                src={filesState.fileToShowURL}
              />
            ) : null}
            {filesState.noPreview ? <span>Preview not available</span> : null}
            {filesState.downloadingFile ? <Spinner centered={true} /> : null}
          </div>
          <div className="rotate-controls">
            <Button className='btn-rotate rotate-right' onClick={rotateRight} aria-label='Rotate right'>
              <IconRotate />
            </Button>
            <Button className='btn-rotate rotate-left' onClick={rotateLeft} aria-label='Rotate left'>
              <IconRotateFlipped />
            </Button>
          </div>
        </div>
        {imgExpanded ? (
          <div className='file-preview-big' onClick={toggleExpandImg}>
            <div
              className={'preview-window'}
              style={{
                transform:
                  'rotate(' + rotation + 'deg) scale(' + getBigScale() + ')',
              }}
            >
              {filesState.fileToShowURL ? (
                <img
                  ref={bigImgRef}
                  alt='preview-big'
                  onClick={toggleExpandImg}
                  src={filesState.fileToShowURL}
                />
              ) : null}
              {filesState.noPreview ? <span>Preview not available</span> : null}
              {filesState.downloadingFile ? <Spinner centered={true} /> : null}
            </div>
            <Button className='btn-rotate rotate-right' onClick={rotateRight} aria-label='Rotate right'>
              <IconRotate />
            </Button>
            <Button className='btn-rotate rotate-left' onClick={rotateLeft} aria-label='Rotate left'>
              <IconRotateFlipped />
            </Button>
          </div>
        ) : null}
      </div>
      <div className='file-upload'>
        <h2>Upload a new file</h2>
        <table>
          <thead>
            <tr>
              <th>Filesize limit</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>20MB per file</td>
            </tr>
          </tbody>
        </table>
        <div
          className='file-dropper'
          onDrop={dropHandler}
          onDragOver={dragOver}
          onDragLeave={dragLeave}
          onDragEnter={dragEnter}
          ref={dragboxRef}
        >
          <DashedBox />
          <div className='upload-btn-wrapper'>
            <p>Drag and drop your file here</p>
            <span>or </span>
            <Button onClick={openUploadModal} className='btn'>
              Click here to upload
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

//  .docx - application/vnd.openxmlformats-officedocument.wordprocessingml.document
